import { usePageHeader } from "contexts/PageHeaderContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  // Calendar,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Check,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  CalendarDays,
} from "lucide-react";
import { Checkbox } from "../../ui/checkbox";
import { toast } from "sonner";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import useDebounce from "hooks/useDebounce";
import { FullScreenLoading } from "components/ui/loader";
import {
  CreateLeaveTypePayload,
  FormErrors,
  LeaveType,
  LeaveTypeGenderRestriction,
} from "types/leaveTypeMaster";
import {
  createLeaveType,
  deleteLeaveType,
  getLeaveTypeList,
  updateLeaveType,
} from "services/LeaveTypeMasterService";
import Pagination from "ui/pagination";
import axios from "axios";
import ConfirmDialog from "ui/ConfirmDialog";
import { removeSpecialCharacters } from "utils/commonutils";

interface LeaveTypeMasterPageProps {
  onNavigate?: (page: string) => void;
}

export default function LeaveTypeMasterPage({
  onNavigate,
}: LeaveTypeMasterPageProps) {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Leave Type Master",
      childTitle: "Configure and manage employee leave types and policies",
      icon: <CalendarDays className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  // const [showStatistics, setShowStatistics] = useState(false);
  const [editingItem, setEditingItem] = useState<LeaveType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // const [ToDelete, setToDelete] = useState<string | null>(null);
  const [ToDelete, setToDelete] = useState<LeaveType | null>(null);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [LeaveTypeTotalCount, setLeaveTypeTotalCount] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
    }),
    [filters, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  const [formData, setFormData] = useState({
    name: "",
    carryForward: false,
    paidLeave: true,
    requiresApproval: true,
    requiresAttachment: false,
    halfDayAllowed: false,
    encashable: false,
    genderRestriction: "" as LeaveTypeGenderRestriction | "",
    minimumServiceRequired: "0",
    status: "active" as "active" | "inactive",
    color: "#3B82F6",
    code: "",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({
    name: "",
    code: "",
    color: "",
    status: "",
  });

  // USEEFFECT FUNCTION START
  const fetchLeaveTypes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLeaveTypeList(debouncedFilters);
      const normalizedItems: LeaveType[] = (data?.data?.data || []).map(
        (item) => ({
          id: item.publicId,
          publicId: item.publicId,
          name: item.leaveName,
          leaveTypeName: item.leaveName,
          code: item.leaveCode,
          color: item.color,
          paidLeave: item.isPaidLeave,
          requiresApproval: item.requiresApproval,
          requireApproval: item.requiresApproval,
          requiresAttachment: item.requiresAttachment,
          carryForward: item.isCarryForwardAllowed,
          allowCarryForward: item.isCarryForwardAllowed,
          halfDayAllowed: item.isHalfDayAllowed,
          encashable: item.isEncashable,
          genderRestriction: item.genderRestriction,
          minimumServiceRequired: item.minimumServiceRequired,
          status: item.isActive ? "active" : "inactive",
        }),
      );
      setLeaveTypes(normalizedItems);
      setLeaveTypeTotalCount(data?.data?.totalRecords || 0);
    } catch {
      toast.error("Failed to fetch leave types");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes, refetch]);
  // USEEFFECT FUNCTION END

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedleaveApplicants = useMemo(() => {
    if (!sortColumn) return leaveTypes;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...leaveTypes].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" },
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [leaveTypes, sortColumn, sortDirection]);

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      carryForward: false,
      paidLeave: true,
      requiresApproval: true,
      requiresAttachment: false,
      halfDayAllowed: false,
      encashable: false,
      genderRestriction: "",
      minimumServiceRequired: "0",
      status: "active",
      color: "#3B82F6",
      code: "",
    });
    setFormErrors({
      name: "",
      code: "",
      color: "",
      status: "",
    });
    setShowDialog(true);
  };

  const handleEdit = (leave: LeaveType) => {
    setEditingItem(leave);
    setFormData({
      name: String(leave?.leaveTypeName),
      color: leave?.color,
      code: leave?.code,
      carryForward: Boolean(leave?.allowCarryForward),
      paidLeave: Boolean(leave?.paidLeave),
      requiresApproval: Boolean(leave?.requireApproval),
      requiresAttachment: leave.requiresAttachment ?? false,
      halfDayAllowed: leave.halfDayAllowed ?? false,
      encashable: leave.encashable ?? false,
      genderRestriction:
        leave.genderRestriction === "MaleOnly" ||
        leave.genderRestriction === "FemaleOnly"
          ? leave.genderRestriction
          : "",
      minimumServiceRequired: String(leave.minimumServiceRequired ?? 0),
      status: leave.status === "active" ? "active" : "inactive",
    });
    setFormErrors({
      name: "",
      code: "",
      color: "",
      status: "",
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (saving) return;
    const errors: FormErrors = {
      name: "",
      code: "",
      color: "",
      status: "",
    };

    if (!formData.name.trim()) {
      errors.name = "Leave type name is required";
    }

    if (!formData.code.trim()) {
      errors.code = "Code is required";
    }

    if (!formData.color.trim()) {
      errors.color = "Color is required";
    }

    if (
      !formData.minimumServiceRequired.trim() ||
      !Number.isInteger(Number(formData.minimumServiceRequired)) ||
      Number(formData.minimumServiceRequired) < 0 ||
      Number(formData.minimumServiceRequired) > 2147483647
    ) {
      errors.minimumServiceRequired =
        "Enter a whole number between 0 and 2147483647";
    }

    if (!formData.status) {
      errors.status = "Status is required";
    }

    const genderRestriction = formData.genderRestriction;
    if (
      genderRestriction !== "MaleOnly" &&
      genderRestriction !== "FemaleOnly"
    ) {
      errors.genderRestriction = "Select a gender restriction";
      setFormErrors(errors);
      return;
    }

    setFormErrors(errors);

    if (
      errors.name ||
      errors.code ||
      errors.color ||
      errors.status ||
      errors.minimumServiceRequired
    ) {
      return;
    }

    try {
      setSaving(true);
      const payload: CreateLeaveTypePayload = {
        leaveName: formData.name,
        leaveCode: formData.code,
        color: formData.color,
        isPaidLeave: formData.paidLeave,
        requiresAttachment: formData.requiresAttachment,
        requiresApproval: formData.requiresApproval,
        isHalfDayAllowed: formData.halfDayAllowed,
        isCarryForwardAllowed: formData.carryForward,
        isEncashable: formData.encashable,
        genderRestriction,
        minimumServiceRequired: Number(formData.minimumServiceRequired),
        isActive: formData.status === "active",
      };
      if (editingItem) {
        if (!editingItem.publicId) {
          toast.error("Cannot update leave type: public ID is missing");
          return;
        }
        await updateLeaveType(editingItem.publicId, payload);
        toast.success("Leave type updated successfully");
      } else {
        await createLeaveType(payload);
        toast.success("Leave type added successfully");
      }
      setRefetch((prev) => !prev);
      setShowDialog(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message =
          error.response.data.message || "Failed to save leave type";
        toast.error(message);
      } else {
        toast.error("Failed to save leave type");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (leave: LeaveType) => {
    setToDelete(leave);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (!ToDelete || deleting) return;
    if (!ToDelete.publicId) {
      toast.error("Cannot delete leave type: public ID is missing");
      return;
    }
    try {
      setDeleting(true);
      await deleteLeaveType(ToDelete.publicId);
      setLeaveTypes(leaveTypes.filter((l) => l.publicId !== ToDelete.publicId));
      toast.success("Leave type deleted successfully");
      setDeleteDialogOpen(false);
      setToDelete(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteDraft = () => {
    setDeleteDialogOpen(false);
    setToDelete(null);
  };

  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative w-full sm:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />

          <Input
            placeholder="Search by leave name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setFilters((prev) => ({ ...prev, pageNo: 1 }));
            }}
            className="pl-10 w-full"
          />
        </div>

        <div className="w-full sm:w-auto">
          <Button
            onClick={handleAdd}
            style={{ background: "#5B9BD5" }}
            className="text-white w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Leave Type
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {/* {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Leave Types</p>
                  <p className="text-2xl font-bold text-blue-900">{leaveTypes.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Active Policies</p>
                  <p className="text-2xl font-bold text-green-900">
                    {leaveTypes.filter(l => l.status === 'active').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Paid Leaves</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {leaveTypes.filter(l => l.paidLeave).length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <CalendarDays className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Total Days/Year</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {leaveTypes.filter(l => l.paidLeave).reduce((sum, l) => sum + l.daysAllowed, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )} */}

      {/* Leave Types Table */}
      <Card className="border-[#5B9BD5]/30 bg-white">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 p-4 rounded-t-xl">
          <CardTitle style={{ color: "#5B9BD5" }}>Leave Types</CardTitle>
          <CardDescription>
            {leaveTypes.length} leave type{leaveTypes.length !== 1 ? "s" : ""}{" "}
            found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Code
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("leaveTypeName")}
                  >
                    <div className="flex items-center gap-1">
                      Leave Type
                      {sortColumn === "leaveTypeName" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="w-3 h-3" />
                        ) : (
                          <ArrowDown className="w-3 h-3" />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Encashable
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Half Day Allowed
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Carry Forward
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Type
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Approval
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Status
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-right"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaveTypes?.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-8 text-gray-500"
                    >
                      No leave types found
                    </TableCell>
                  </TableRow>
                ) : (
                  leaveTypes &&
                  sortedleaveApplicants?.map((leave, index) => (
                    <TableRow key={leave.id} className="hover:bg-gray-50">
                      <TableCell className="px-4 py-3 text-xs text-gray-500">
                        {leave?.code}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: leave.color }}
                          />
                          <span className="font-medium">
                            {leave?.leaveTypeName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {leave.encashable ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check className="w-3 h-3" />
                            <span>Yes</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-gray-400">
                            <X className="w-3 h-3" />
                            <span>No</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {leave.halfDayAllowed ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check className="w-3 h-3" />
                            <span>Yes</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-gray-400">
                            <X className="w-3 h-3" />
                            <span>No</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {leave.allowCarryForward ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <Check className="w-3 h-3" />
                            <span>
                              {leave.maxCarryForward != null
                                ? `${leave.maxCarryForward} days`
                                : "Yes"}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-gray-400">
                            <X className="w-3 h-3" />
                            <span>No</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        <Badge
                          className={
                            leave.paidLeave
                              ? "bg-green-100 text-green-700 border border-green-300 text-xs px-2"
                              : "bg-gray-100 text-gray-700 border border-gray-300 text-xs px-2"
                          }
                        >
                          {leave.paidLeave ? "Paid" : "Unpaid"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {leave?.requireApproval ? (
                          <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs px-2">
                            Required
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-xs px-2">
                            Auto
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs">
                        {leave.status === "active" ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-300 text-xs px-2">
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-rose-50 text-rose-700 border border-rose-300 text-xs px-2">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(leave)}
                            className="h-8 px-2 hover:bg-sky-50"
                          >
                            <Edit2 className="w-4 h-4 text-[#5B9BD5]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(leave)}
                            className="h-8 px-2 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* PAGINATION COMPONENT FOR DRAFT */}
          <Pagination
            totalItems={LeaveTypeTotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page,
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1,
              }))
            }
            label="Leave Types Pagination"
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          if (!saving) setShowDialog(open);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Leave Type" : "Add New Leave Type"}
            </DialogTitle>
            <DialogDescription>
              {editingItem
                ? "Update the leave type details below"
                : "Fill in the details to create a new leave type"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  type="text"
                  label="Leave Type Name"
                  required
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const value = removeSpecialCharacters(e.target.value);
                    setFormData({ ...formData, name: value });
                    if (e.target.value) {
                      setFormErrors({ ...formErrors, name: "" });
                    }
                  }}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs">{formErrors.name}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  type="text"
                  label="Code"
                  required
                  id="code"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData({ ...formData, code: e.target.value });
                    if (e.target.value) {
                      setFormErrors({ ...formErrors, code: "" });
                    }
                  }}
                />
                {formErrors.code && (
                  <p className="text-red-500 text-xs">{formErrors.code}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  type="color"
                  label="Color"
                  required
                  id="color"
                  value={formData.color}
                  onChange={(e) => {
                    setFormData({ ...formData, color: e.target.value });
                    if (e.target.value) {
                      setFormErrors({ ...formErrors, color: "" });
                    }
                  }}
                />
                {formErrors.color && (
                  <p className="text-red-500 text-xs">{formErrors.color}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <SelectFieldset label="Status" required>
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") => {
                      setFormData({ ...formData, status: value });
                      if (value) {
                        setFormErrors({ ...formErrors, status: "" });
                      }
                    }}
                  >
                    <SelectTriggerUI
                      className={formErrors.status ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select Status" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {formErrors.status && (
                  <p className="text-red-500 text-xs">{formErrors.status}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <SelectFieldset label="Gender Restriction" required>
                  <Select
                    value={formData.genderRestriction}
                    onValueChange={(value: LeaveTypeGenderRestriction) => {
                      setFormData({ ...formData, genderRestriction: value });
                      setFormErrors({ ...formErrors, genderRestriction: "" });
                    }}
                  >
                    <SelectTriggerUI
                      aria-label="Gender Restriction"
                      aria-invalid={!!formErrors.genderRestriction}
                      aria-describedby={
                        formErrors.genderRestriction
                          ? "gender-restriction-error"
                          : undefined
                      }
                    >
                      <SelectValue placeholder="Select Gender" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="MaleOnly">Male Only</SelectItem>
                      <SelectItem value="FemaleOnly">Female Only</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {formErrors.genderRestriction && (
                  <p
                    id="gender-restriction-error"
                    className="text-red-500 text-xs"
                  >
                    {formErrors.genderRestriction}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  type="number"
                  label="Minimum Service Required"
                  id="minimumServiceRequired"
                  min={0}
                  max={2147483647}
                  step={1}
                  value={formData.minimumServiceRequired}
                  aria-invalid={!!formErrors.minimumServiceRequired}
                  aria-describedby={
                    formErrors.minimumServiceRequired
                      ? "minimum-service-error"
                      : undefined
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      minimumServiceRequired: e.target.value,
                    });
                    setFormErrors({
                      ...formErrors,
                      minimumServiceRequired: "",
                    });
                  }}
                />
                {formErrors.minimumServiceRequired && (
                  <p
                    id="minimum-service-error"
                    className="text-red-500 text-xs"
                  >
                    {formErrors.minimumServiceRequired}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.paidLeave}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, paidLeave: !!checked })
                  }
                />
                <span className="text-sm font-medium">Paid Leave</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.requiresApproval}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, requiresApproval: !!checked })
                  }
                />
                <span className="text-sm font-medium">Requires Approval</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(
                [
                  ["requiresAttachment", "Requires Attachment"],
                  ["halfDayAllowed", "Half Day Allowed"],
                  ["encashable", "Encashable"],
                ] as const
              ).map(([field, label]) => (
                <label
                  key={field}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Checkbox
                    checked={formData[field]}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, [field]: checked === true })
                    }
                  />
                  <span className="text-sm font-medium">{label}</span>
                </label>
              ))}

              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <Checkbox
                  checked={formData.carryForward}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, carryForward: !!checked })
                  }
                />
                <span className="text-sm font-medium">Allow Carry Forward</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setShowDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              {saving && (
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              <span>
                {saving
                  ? "Processing..."
                  : `${editingItem ? "Update" : "Create"} Leave Type`}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={false}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!deleting) setDeleteDialogOpen(open);
        }}
        loading={deleting}
        title="Delete Leave Type"
        description={
          <>
            Are you sure you want to delete{" "}
            <span className="font-semibold">"{ToDelete?.leaveTypeName}"</span>?
            This action cannot be undone.
          </>
        }
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        onConfirm={confirmDeleteDraft}
        onCancel={cancelDeleteDraft}
      />
      {/* Delete Confirmation Dialog */}
    </div>
  );
}
